import React, {useCallback, useContext, useMemo} from 'react';
import {
  Text,
  View,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  Share,
  Platform,
  Dimensions,
} from 'react-native';

import {FontAwesome, MaterialIcons} from '@expo/vector-icons';
import {useTranslation} from 'react-i18next';
import * as Sentry from 'sentry-expo';
import {useNavigation} from '@react-navigation/native';

import Api from '../../../constants/Api';
import {ThemeContext, WEB_MAX_WIDTH} from '../../../contexts/ThemeContext';
import {formatDate, formatTime} from '../../../helpers/dateHelper';
import {debounce} from '../../../utils/debounce';

import Card from './Card';
import Bubble from './Bubble';
import ResponseLinks from './ResponseLinks';
import PlacesMap from './PlacesMap';
import {UserContext} from '../../../contexts/UserContext';

const CHANNEL_ICONS = {
  Recording: <FontAwesome name="microphone" size={14}/>,
  recording: <FontAwesome name="microphone" size={14}/>,
  Web: <FontAwesome name="laptop" size={14}/>,
  web: <FontAwesome name="laptop" size={14}/>,
  Whatsapp: <FontAwesome name="whatsapp" size={16}/>,
  whatsapp: <FontAwesome name="whatsapp" size={16}/>,
  Email: <FontAwesome name="envelope-o" size={13}/>,
  email: <FontAwesome name="envelope-o" size={13}/>,
  Slack: <FontAwesome name="slack" size={14}/>,
  slack: <FontAwesome name="slack" size={14}/>,
  Telegram: <FontAwesome name="telegram" size={14}/>,
  telegram: <FontAwesome name="telegram" size={14}/>,
  Keyboard: <FontAwesome name="keyboard-o" size={14}/>,
  keyboard: <FontAwesome name="keyboard-o" size={14}/>,
  Sms: <MaterialIcons name="sms" size={14}/>,
  sms: <MaterialIcons name="sms" size={14}/>,
  Message: <MaterialIcons name="sms" size={14}/>,
  message: <MaterialIcons name="sms" size={14}/>,
};

const Chat = ({
  scrollViewRef,
  onScrollBeginDrag,
  queries,
  setQueryText,
  handleReprocessQuery,
  exampleSections,
  canStopGenerating,
  disableResponseLinks = false,
  disableQueryLinks = false,
}) => {
  const {t} = useTranslation();
  const theme = useContext(ThemeContext);
  const styles = useMemo(() => getStyles(theme), [theme]);
  const {navigate} = useNavigation();
  const {user} = useContext(UserContext);

  let currentDate = queries.length ? new Date(queries[0].created_at) : null;

  const handleShare = useCallback((query, response) => debounce(async () => {
    try {
      let cardsText = '';

      if (response.cards.length) {
        if (response.cards.length === 1) {
          // TODO: Handle weather and shazam cards
          const card = response.cards[0];

          if (card.card_type === 'image') {
            cardsText = `\n${card.image_url}`;
          }
        } else {
          const texts = response.cards.reduce((result, card) => {
            if (card.card_type === 'place') {
              result.push(`- ${card.title} (${card.link_1_url})`);
            }

            return result;
          }, []);

          if (texts.length) {
            cardsText = `\n\n${texts.join('\n')}`;
          }
        }
      }

      const result = await Share.share({
        message: `${response.message}${cardsText}\n\n${t('MainScreen.ChatView.Chat.sharedByText', 'Message shared via {{appName}}.', {appName: Api.appName})}`,
      }, {
        subject: query.text,
      });

      if (result.action === Share.sharedAction) {
        if (result.activityType) {
          // shared with activity type of result.activityType
        } else {
          // shared
        }
      } else if (result.action === Share.dismissedAction) {
        // dismissed
      }
    } catch (error) {
      // TODO: Handle errors
      if (Platform.OS === 'web') {
        Sentry.Browser.captureException(error);
      } else {
        Sentry.Native.captureException(error);
      }
    }
  }, 300, true), [t]);

  return (
    <ScrollView
      ref={scrollViewRef}
      onScrollBeginDrag={onScrollBeginDrag}
      scrollEventThrottle={0}
      contentContainerStyle={{
        alignSelf: 'center',
        maxWidth: WEB_MAX_WIDTH,
        width: '100%',
        flexDirection: 'column',
        paddingHorizontal: 12,
        paddingBottom: canStopGenerating ? 48 : 8,
      }}
    >
      {!(queries || []).length && (exampleSections || []).length ? (
        <View style={styles.examplesContainer}>
          <Text style={[styles.text, {maxWidth: 300, alignSelf: 'center'}]}>
            {t(
              'MainScreen.ChatView.Chat.exampleIntroduction',
              'Here are some examples of the things you can ask :'
            )}
          </Text>

          {exampleSections.map(({title, examples}) => (
            <View key={title}>
              <Text style={{color: theme.textPrimary, fontSize: 22, fontWeight: 200, marginVertical: 12, textAlign: 'center'}}>
                {title}
              </Text>

              {(examples || []).length > 0 && (
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={Platform.OS === 'web'}
                  style={{flexGrow: 0}}
                >
                  {examples.map((example, i) => (
                    <TouchableOpacity key={example} onPress={() => setQueryText(example)}>
                      <View
                        style={[{
                          backgroundColor: theme.light ? '#ffffff' : '#383838',
                          borderRadius: 20,
                          width: Math.min(Dimensions.get('window').width, 800) / (Dimensions.get('window').width > 600 ? 3 : 2) - 18,
                          height: 100,
                          justifyContent: 'center',
                          paddingHorizontal: 12,
                        }, {
                          marginRight: i === examples.length - 1 ? 0 : 12,
                        }]}
                      >
                        <Text
                          style={{
                            color: theme.textPrimary,
                            fontSize: 16,
                            lineHeight: 16,
                            textAlign: 'center',
                          }}
                        >
                          {example}
                        </Text>
                      </View>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              )}
            </View>
          ))}
        </View>
      ) : null}

      {queries.map((query, i) => {
        if (Object.keys(query).length === 0) {
          return null;
        }

        let shouldDisplayDate = i === 0;

        const queryDate = new Date(query.created_at);

        if (
          queryDate.getDate() !== currentDate.getDate()
          || queryDate.getMonth() !== currentDate.getMonth()
          || queryDate.getFullYear() !== currentDate.getFullYear()
        ) {
          currentDate = queryDate;
          shouldDisplayDate = true;
        }

        // if (query.status === 'pending') {
        //   const fakeResponseId = uuid.v4();
        //
        //   query.responses.push({
        //     id: fakeResponseId,
        //     message: null,
        //     created_at: Date.now(),
        //     channel: query.channel,
        //     source: undefined,
        //     cards: [],
        //   });
        // }

        return (
          <View key={`${query.ref}-${query.id}`}>
            {shouldDisplayDate && (
              <Bubble variant="date" text={formatDate(currentDate, t)}/>
            )}

            {!user.expert_mode || disableQueryLinks || query.status === 'processing'
              ? (
                <Bubble
                  variant={query.status === 'empty' ? 'errorQuery' : 'query'}
                  text={query.text}
                />
              )
              : (
                <TouchableOpacity onPress={() => navigate('Query', {id: query.id})}>
                  <Bubble
                    variant={query.status === 'empty' ? 'errorQuery' : 'query'}
                    text={query.text}
                  />
                </TouchableOpacity>
              )}


            <Text style={{fontSize: 11, color: theme.textSecondary, alignSelf: 'flex-end', marginRight: 12}}>
              {CHANNEL_ICONS[query.channel]} {formatTime(queryDate)}
            </Text>

            {query.status === 'empty'
              ? null
              : !query.responses || !query.responses.length
                ? queryDate.getTime() > Date.now() - 60000
                  // Note: Loading
                  ? query.text ? <Bubble variant="response"/> : null
                  // Note: Timed out
                  : (
                    <Bubble
                      variant="response"
                      text={t('MainScreen.ChatView.Chat.timeoutBubble', 'I am sorry, but I wasn\'t able to process this query.')}
                    />
                  )
                // Note: Actual responses
                : query.responses.sort((a, b) => a.id - b.id).map(response => (
                  <View key={response.id}>
                    <View key={response.id} style={{flexDirection: 'row', alignItems: 'center'}}>
                      <Bubble
                        variant="response"
                        text={response.message}
                      >
                        <ResponseLinks
                          query={query}
                          response={response}
                          handleReprocessQuery={handleReprocessQuery}
                          disabled={disableResponseLinks}
                        />
                      </Bubble>

                      <TouchableOpacity
                        style={{width: 40, height: 40, alignItems: 'center', justifyContent: 'center'}}
                        onPress={handleShare(query, response)}
                      >
                        <View style={{borderRadius: 20, padding: 6, backgroundColor: theme.light ? '#ffffff' : '#383838'}}>
                          <FontAwesome name="share" size={14} color={theme.colorSecondary}/>
                        </View>
                      </TouchableOpacity>
                    </View>

                    {(response.cards || []).length > 0 && response.cards.find(({card_type}) => card_type === 'place') && (
                      <PlacesMap cards={response.cards}/>
                    )}

                    {(response.cards || []).length > 0 && (
                      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{flexGrow: 0}}>
                        {response.cards.map((card, i) => (
                          <Card
                            key={card.id}
                            card={card}
                            isLast={i === response.cards.length - 1}
                          />
                        ))}
                      </ScrollView>
                    )}

                    <Text style={{marginTop: 5, fontSize: 11, color: theme.textSecondary, alignSelf: 'flex-start', marginLeft: 12}}>
                      <>{CHANNEL_ICONS[response.channel]}  {formatTime(new Date(response.created_at))}{response.source ? ` ${t('MainScreen.ChatView.Chat.source', '- Source: {{source}}', {source: response.source})}`: ''}</>
                    </Text>
                  </View>
                ))
            }
          </View>
        );
      })}
    </ScrollView>
  );
};

const getStyles = theme => StyleSheet.create({
  examplesContainer: {
    flex: 1,
    paddingTop: 30,
  },
  text: {
    textAlign: 'center',
    color: theme.textPrimary,
    fontSize: 16,
  },
});

export default Chat;
